import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import BasicDateRangePicker from '@mui/lab/DateRangePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DATE_FORMAT } from '@cyberint/common-utils';
import { Orientation, PopperPlacements, INPUT_PROPS } from './consts';
import * as S from './styled';

const POPPER_SX = {
  sx: {
    '& .MuiPaper-root': {
      backgroundColor: 'var(--calendar-popup-bg, #fff)',
      color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))'
    },
    '& .MuiPickersDay-root:not(.Mui-disabled), & .MuiDateRangePickerDay-day:not(.Mui-disabled)': {
      color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))'
    },
    // MUI paints idle days with theme.palette.background.paper (white); the popup surface shows through instead
    '& .MuiPickersDay-root:not(.Mui-selected)': { backgroundColor: 'transparent' },
    '& .MuiPickersDay-root:not(.Mui-selected):hover': {
      backgroundColor: 'var(--calendar-popup-hover-bg, rgba(0, 0, 0, 0.04))'
    },
    '& .MuiIconButton-root': { color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.54))' },
    // clock numbers are an unclassed span (PrivateClockNumber) — only role/aria identify them
    '& [role="option"]:not([aria-selected="true"]):not([aria-disabled="true"])': {
      color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))'
    },
    '& .MuiDivider-root': { borderColor: 'var(--calendar-popup-divider-color, rgba(0, 0, 0, 0.12))' },
    '& .MuiPickersDay-today': { borderColor: 'var(--calendar-popup-color, currentColor)' },
    '& .MuiTab-root:not(.Mui-selected)': { color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))' },
    '& .MuiPickersCalendarHeader-label': {
      color: 'var(--calendar-popup-color, inherit)'
    },
    '& .MuiTypography-caption[aria-hidden="true"]': { color: 'var(--calendar-popup-muted-color, rgba(0, 0, 0, 0.6))' },
    '& .MuiDateRangePickerViewDesktop-rangeCalendarContainer': {
      borderColor: 'var(--calendar-popup-divider-color, rgba(0, 0, 0, 0.12))'
    },
    '& .MuiDateRangePickerDay-day.Mui-selected, & .MuiPickersDay-root.Mui-selected': {
      color: 'var(--calendar-day-color-selected, #fff)'
    },
    '& .MuiDateRangePickerDay-rangeIntervalDayHighlight': { backgroundColor: 'var(--calendar-range-bg)' },
    '& .MuiDateRangePickerDay-day.Mui-selected': {
      backgroundColor: 'var(--calendar-day-bg-selected)'
    },
    '& .MuiDateRangePickerDay-day.Mui-selected:hover': {
      backgroundColor: 'var(--calendar-day-bg-selected-hover)'
    },
    '& .MuiDateRangePickerDay-day.Mui-selected:focus': {
      backgroundColor: 'var(--calendar-day-bg-selected-hover)'
    }
  }
};

const DateRangePicker = ({
  startText = 'From',
  endText = 'To',
  showConnector,
  onChange,
  onClose,
  isOpen,
  value,
  dateFormat = DATE_FORMAT.MMM_DD_YYYY_FORMAT,
  placement = PopperPlacements.AUTO,
  orientation = Orientation.HOR,
  calendars = 1,
  inputWidth
}) => {
  const computedValue = useMemo(
    () => (Array.isArray(value) ? value.map(v => (typeof v === 'string' ? new Date(v) : v)) : value),
    [value]
  );
  const popperProps = useMemo(() => ({ placement, ...POPPER_SX }), [placement]);

  const onChangeHandler = useCallback(
    val => {
      const mapped = Array.isArray(val) ? val.map(v => (typeof v?.valueOf === 'function' ? v.valueOf() : v)) : val;
      onChange(mapped);
    },
    [onChange]
  );

  const inputRenderer = useCallback(
    (startProps, endProps) => (
      <S.InputContainer $orientation={orientation}>
        <S.TextFieldWrapper
          className="dateRangeFromInput"
          {...startProps}
          size="small"
          $inputWidth={inputWidth}
          InputProps={INPUT_PROPS}
        />
        {showConnector && <S.Connector> – </S.Connector>}
        <S.TextFieldWrapper
          className="dateRangeToInput"
          {...endProps}
          size="small"
          $inputWidth={inputWidth}
          InputProps={INPUT_PROPS}
        />
      </S.InputContainer>
    ),
    [orientation, showConnector, inputWidth]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BasicDateRangePicker
        startText={startText}
        endText={endText}
        value={computedValue}
        onChange={onChangeHandler}
        calendars={calendars}
        PopperProps={popperProps}
        disableMaskedInput
        format={dateFormat}
        inputFormat={dateFormat}
        renderInput={inputRenderer}
        onClose={onClose}
        open={isOpen}
      />
    </LocalizationProvider>
  );
};

DateRangePicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]))
    .isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  startText: PropTypes.string,
  endText: PropTypes.string,
  showConnector: PropTypes.bool,
  isOpen: PropTypes.bool,
  dateFormat: PropTypes.string,
  placement: PropTypes.string,
  orientation: PropTypes.string,
  calendars: PropTypes.number,
  inputWidth: PropTypes.string
};

export default DateRangePicker;
